import React, { useState } from 'react';
import {
  X, ChevronDown, ChevronRight,
  Sparkles, Bug, Palette, Package, ShoppingCart,
  Calendar, Stethoscope, Network, Shield, Info,
  Users, BookOpen, Briefcase
} from 'lucide-react';
import changelog from '../data/changelog.json';

// Map iconName (string) → composant lucide-react
const ICON_MAP = {
  Sparkles, Bug, Palette, Package, ShoppingCart,
  Calendar, Stethoscope, Network, Shield, Info,
  Users, BookOpen, Briefcase
};

const resolveIcon = (name) => ICON_MAP[name] || Info;

const ChangelogModal = ({ onClose }) => {
  const versions = changelog.versions || [];
  const defaultOpen = versions[0]?.version ? [versions[0].version] : [];
  const [expandedVersions, setExpandedVersions] = useState(defaultOpen);

  const toggleVersion = (version) => {
    setExpandedVersions(prev =>
      prev.includes(version)
        ? prev.filter(v => v !== version)
        : [...prev, version]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-primary to-primary-dark text-white">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                📋 Historique des versions
              </h2>
              <p className="text-white/70 mt-1">
                Suivez toutes les évolutions de SuiviPlus
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-primary-darker rounded-full transition"
              aria-label="Fermer"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {versions.map((v) => (
            <div key={v.version} className="mb-6 border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleVersion(v.version)}
                className="w-full p-4 bg-gray-50 hover:bg-gray-100 transition flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{v.emoji || '📦'}</span>
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-gray-900">
                      Version {v.version}
                    </h3>
                    <p className="text-sm text-gray-600">{v.date}</p>
                    {v.title && (
                      <p className="text-sm font-medium text-primary-dark mt-1">
                        {v.title}
                      </p>
                    )}
                  </div>
                </div>
                {expandedVersions.includes(v.version) ? (
                  <ChevronDown size={20} className="text-gray-600" />
                ) : (
                  <ChevronRight size={20} className="text-gray-600" />
                )}
              </button>

              {expandedVersions.includes(v.version) && (
                <div className="p-4 bg-white">
                  {v.summary && (
                    <p className="text-sm text-gray-700 mb-4 italic border-l-4 border-primary/30 pl-3">
                      {v.summary}
                    </p>
                  )}

                  {v.highlights && v.highlights.length > 0 && (
                    <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <h4 className="font-semibold text-gray-800 mb-2">À retenir</h4>
                      <ul className="space-y-1">
                        {v.highlights.map((h, i) => (
                          <li key={i} className="text-sm text-gray-700">
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(v.sections || []).map((section, idx) => {
                    const Icon = resolveIcon(section.iconName);
                    return (
                      <div key={idx} className="mb-6 last:mb-0">
                        <div className="flex items-center gap-2 mb-3">
                          <Icon size={18} className={section.color || 'text-primary'} />
                          <h4 className="font-semibold text-gray-800">{section.title}</h4>
                        </div>

                        <div className="space-y-4">
                          {(section.items || []).map((item, itemIdx) => (
                            <div key={itemIdx} className="pl-6 border-l-2 border-gray-200">
                              <div className="mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{item.name}</span>
                                  {item.tag && (
                                    <span className="px-2 py-0.5 bg-primary/10 text-primary-dark text-xs rounded-full font-mono">
                                      {item.tag}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {item.details && (
                                <ul className="space-y-1 text-sm text-gray-600">
                                  {item.details.map((detail, detailIdx) => (
                                    <li key={detailIdx} className="flex items-start gap-2">
                                      <span className="text-primary mt-1">•</span>
                                      <span>{detail}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {item.impact && (
                                <div className="mt-2 p-2 bg-green-50 border-l-4 border-green-500 rounded">
                                  <p className="text-sm text-green-800">
                                    <strong>Impact :</strong> {item.impact}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {changelog.unreleased && (
            <div className="mt-8 p-6 bg-gradient-to-br from-purple-50 to-primary/5 rounded-lg border border-purple-200">
              <h3 className="text-xl font-bold text-purple-900 mb-3 flex items-center gap-2">
                🚀 À venir — prochaine version
              </h3>
              {changelog.unreleased.highlights && (
                <ul className="space-y-1">
                  {changelog.unreleased.highlights.map((h, i) => (
                    <li key={i} className="text-sm text-gray-700">
                      {h}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangelogModal;
